/**
 * useCheckinMonitor — Controlla i check-in periodici delle vedette.
 *
 * Logica:
 *   - Scansione ogni 30 s su tutte le vedette con stato "attiva" e
 *     ultimoCheckin valorizzato.
 *   - Se (now - ultimoCheckin) > checkinIntervalMin → overdue.
 *   - Al primo rilevamento di nuovi overdue → alert audio (3 beep).
 *
 * Restituisce overdueIds per colorare marker e card in rosso pulsante.
 */

import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../store/useAppStore';

// ── Audio alert (Web Audio API — nessuna dipendenza esterna) ───
function playCheckinAlert(): void {
  try {
    const ctx = new AudioContext();
    const beep = (startSec: number, freq: number) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.35, ctx.currentTime + startSec);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startSec + 0.22);
      osc.start(ctx.currentTime + startSec);
      osc.stop(ctx.currentTime + startSec + 0.22);
    };
    beep(0,    880);
    beep(0.30, 880);
    beep(0.60, 1100);
  } catch {
    // AudioContext non disponibile (es. SSR o browser limitato)
  }
}

// ── Hook ──────────────────────────────────────────────────────
export function useCheckinMonitor(): { overdueIds: Set<number> } {
  const lookouts = useAppStore((s) => s.lookouts);
  const [overdueIds, setOverdueIds] = useState<Set<number>>(new Set());
  const prevRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const check = () => {
      const now = Date.now();
      const next = new Set<number>();

      for (const l of lookouts) {
        if (l.stato !== 'attiva' || !l.ultimoCheckin) continue;
        const intervalMs = (l.checkinIntervalMin ?? 15) * 60_000;
        if (now - new Date(l.ultimoCheckin).getTime() > intervalMs) {
          next.add(l.id);
        }
      }

      // Suona solo se ci sono nuovi scaduti rispetto al check precedente
      const hasNew = [...next].some((id) => !prevRef.current.has(id));
      if (hasNew) playCheckinAlert();

      prevRef.current = next;
      setOverdueIds((prev) => {
        if (prev.size === next.size && [...next].every((id) => prev.has(id))) return prev;
        return next;
      });
    };

    check();
    const id = window.setInterval(check, 30_000);
    return () => window.clearInterval(id);
  }, [lookouts]);

  return { overdueIds };
}

// ── Utility esportata per calcolo countdown ───────────────────
export function checkinCountdown(
  ultimoCheckin: string | undefined,
  intervalMin = 15,
): { remainingMin: number; overdue: boolean } | null {
  if (!ultimoCheckin) return null;
  const elapsedMs  = Date.now() - new Date(ultimoCheckin).getTime();
  const intervalMs = intervalMin * 60_000;
  const remaining  = Math.round((intervalMs - elapsedMs) / 60_000);
  return { remainingMin: remaining, overdue: remaining <= 0 };
}
